<template>
    <div class="card capacity-card" :class="{ editable }">
        <VenueSnippet
            v-if="venue && venueID && showVenue"
            class="main-snippet"
            :venue="venue"
            :editable="editable"
            @open="selectVenueOpen = true"
        />
        <div v-if="venueID" class="row progress-row">
            <div class="progress-container">
                <ProgressCircular :value="percentage"></ProgressCircular>
            </div>
            <div v-if="venue" class="text-container copy-m">
                <p class="headline bold">{{ text }}</p>
                <p v-if="venue.capacity === null" class="subline">{{ $t('event.capacity_card.specify_capacity') }}</p>
                <p v-else class="subline">
                    <span class="bold">{{ venue.capacityInUse ? venue.capacityInUse : '0' }}</span>
                    {{ $t('general.from') }} <span class="bold">{{ realMax ? realMax : '0' }}</span>
                    {{ $t('event.capacity_card.places_occupied') }}
                </p>
            </div>

            <div v-if="!showVenue" class="row-actions">
                <div class="row action-row primary" @click="openModalAdd()">
                    <div class="button copy-m bold">{{ $t('venue.capacity_card.actions.add_user') }}</div>
                </div>
                <div class="row action-row" @click="openModalRemove()">
                    <div class="button copy-m bold">{{ $t('venue.capacity_card.actions.remove_user') }}</div>
                </div>
                <div class="row action-row danger" @click="openModalRemoveAll()">
                    <div class="button copy-m bold">{{ $t('venue.capacity_card.actions.remove_all_users') }}</div>
                </div>
                <Button
                    class="download-qr-code"
                    icon="download"
                    :value="$t('venue.capacity_card.actions.download_qr_code')"
                    type="link"
                    :loading="isQRCodeLoading"
                    @click="openQRCode"
                />
            </div>
        </div>
        <div v-if="!venueID" class="add-venue" @click="selectVenueOpen = true">
            <div class="copy-m title">
                <Icon name="night-club-disco-ball" />
                <p>{{ $t('venue.capacity_card.no_venue') }}</p>
            </div>
            <div class="action copy-s bold">
                {{ $t('venue.capacity_card.add_venue') }}
            </div>
        </div>
        <Modal v-if="modalOpen" @cancel="modalOpen = false">
            <div class="modal-content">
                <div class="headline-container">
                    <div class="copy-l bold">{{ modalTitle }}</div>
                </div>
                <div class="main">
                    <div class="description copy-s">
                        {{ $t('venue.capacity_card.modal_description') }}
                    </div>
                </div>
                <div class="action-container fullwidth">
                    <Button type="secondary" :value="$t('general.cancel')" @click="modalOpen = false" />
                    <Button v-if="modalType === 'add'" type="primary" :value="modalTitle" @click="addVisitor()" />
                    <Button v-if="modalType === 'remove'" type="red" :value="modalTitle" @click="removeVisitor()" />
                    <Button
                        v-if="modalType === 'remove-all'"
                        type="red"
                        :value="modalTitle"
                        @click="removeAllVisitors()"
                    />
                </div>
            </div>
        </Modal>
        <Modal v-if="selectVenueOpen" class="modal-select-venue" @cancel="closeSelectVenue">
            <div class="headline-container">
                <div class="title copy-l bold">{{ $t('event.capacity_card.select_venue') }}</div>
                <div class="copy-s">{{ $t('event.capacity_card.choose_venue_for_event') }}</div>
            </div>
            <div v-if="venues" class="modal-main-content">
                <div
                    v-for="venue in venues"
                    :key="venue.venue.id"
                    class="venue-item"
                    @click="selectVenue(venue.venue.id)"
                >
                    <VenueSnippet :venue="venue.venue"></VenueSnippet>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import ProgressCircular from '@/components/ProgressCircular'
import Modal from '@/components/Modal'
import Button from '@/components/Button'
import VenueSnippet from '@/components/VenueSnippet'
import Icon from '@/components/Icon'
import gql from 'graphql-tag'

export default {
    name: 'CapacityCard',
    components: {
        ProgressCircular,
        Modal,
        Button,
        VenueSnippet,
        Icon,
    },
    props: {
        current: {
            type: Number,
            required: true,
        },
        max: {
            type: Number,
            default: 0,
        },
        venueID: {
            type: [Number, String],
            required: true,
        },
        venues: {
            type: Array,
            default: () => [],
        },
        showVenue: {
            type: Boolean,
            default: true,
        },
        showAddUser: {
            type: Boolean,
            default: true,
        },
        event: {
            type: Object,
            default: () => {},
        },
        editable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            modalOpen: false,
            modalType: '',
            selectVenueOpen: false,
            setting: {
                name: 'name',
                type: 'text',
                value: '',
                description: this.$t('event.capacity_card.event_title'),
                empty: this.$t('event.capacity_card.event_title'),
                icon: '',
            },
            isQRCodeLoading: false,
        }
    },
    apollo: {
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        name
                        logoUrl
                        priceLevel
                        capacity
                        sourceStarRating
                        capacityUsage
                        capacityInUse
                    }
                }
            `,
            variables() {
                return {
                    id: this.venueID,
                }
            },
            pollInterval: 5000,
        },
    },
    computed: {
        modalTitle() {
            switch (this.modalType) {
                case 'add':
                    return this.$t('venue.capacity_card.actions.add_user')
                case 'remove':
                    return this.$t('venue.capacity_card.actions.remove_user')
                case 'remove-all':
                    return this.$t('venue.capacity_card.actions.remove_all_users')
                default:
                    return this.$t('venue.capacity_card.actions.modal_default_title')
            }
        },
        percentage() {
            return this.venue ? this.venue.capacityUsage * 100 : 0
        },
        realMax() {
            if (this.venue) {
                return this.venue.capacity ? this.venue.capacity : this.max
            }

            return this.max
        },
        text() {
            if (this.realMax === null) return this.$t('venue.capacity_card.space.no_capacity')
            if (this.percentage < 80) return this.$t('venue.capacity_card.space.enough_space')
            if (this.percentage >= 80 && this.percentage < 100) {
                return this.$t('venue.capacity_card.space.almost_full')
            }
            return this.$t('venue.capacity_card.space.full')
        },
    },
    methods: {
        async openQRCode() {
            this.isQRCodeLoading = true

            try {
                const res = await this.$apollo.mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            generateVenueCheckinPDF(venueID: $id, webCheckinFallback: true)
                        }
                    `,
                    variables: {
                        id: this.venue.id,
                    },
                })

                this.downloadURI(res.data.generateVenueCheckinPDF, `${this.venue.name} Check-in QR Code.pdf`)
            } catch (_) {
                this.$pm.createToast('error', this.$t('errors.qr_code_could_not_be_opened'))
            }

            this.isQRCodeLoading = false
        },
        downloadURI(uri, name) {
            let link = document.createElement('a')
            link.download = name
            link.href = uri
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        openModalAdd() {
            this.openModal('add')
        },
        openModalRemove() {
            this.openModal('remove')
        },
        openModalRemoveAll() {
            this.openModal('remove-all')
        },
        openModal(type) {
            this.modalType = type
            this.modalOpen = true
        },
        async addVisitor() {
            this.modalOpen = false

            try {
                await this.$apollo.mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            incrementVenueManualCheckins(venueID: $id) {
                                id
                                capacityUsage
                                capacityInUse
                            }
                        }
                    `,
                    variables: {
                        id: this.venueID,
                    },
                })
                this.$emit('addVisitor')
            } catch (_) {
                this.$pm.createToast('error', this.$t('errors.user_could_not_be_added'))
            }
        },
        async removeVisitor() {
            this.modalOpen = false

            try {
                await this.$apollo.mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            decrementVenueManualCheckins(venueID: $id) {
                                id
                                capacityUsage
                                capacityInUse
                            }
                        }
                    `,
                    variables: {
                        id: this.venueID,
                    },
                })
                this.$emit('addVisitor')
            } catch (_) {
                this.$pm.createToast('error', this.$t('errors.user_could_not_be_removed'))
            }
        },
        async removeAllVisitors() {
            this.modalOpen = false

            try {
                await this.$apollo.mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            checkoutAllVenueUsers(venueID: $id)
                        }
                    `,
                    variables: {
                        id: this.venueID,
                    },
                })
                this.$emit('addVisitor')
                this.venue.capacityInUse = 0
                this.venue.capacityUsage = 0
            } catch (_) {
                this.$pm.createToast('error', this.$t('errors.all_users_could_not_be_removed'))
            }
        },
        selectVenue(id) {
            this.$emit('venueselect', id)
            this.selectVenueOpen = false
        },
        closeSelectVenue() {
            this.selectVenueOpen = false
        },
    },
}
</script>

<style scoped lang="sass">
.capacity-card
    overflow: hidden
    display: flex
    flex-direction: column

    &.editable
        .main-snippet
            cursor: pointer

    .modal-select-venue

        .headline-container
            padding: $m

        .modal-main-content
            padding: 6px

            .venue-item + .venue-item
                margin-top: $s
            .venue-item
                padding: $s
                border-radius: 10px
                width: 100%
                max-width: 305px
                background: white
                &:hover
                    box-shadow: $precise
                    cursor: pointer

    .add-venue
        display: flex
        justify-content: space-between
        padding: $m $l
        color: $neutral-2
        align-items: center
        cursor: pointer


        .title
            display: flex
            align-items: center

            .icon
                margin-right: $s

    .main-snippet.venue-snippet
        padding: $m
        border-bottom: 1px solid $gray-2

    .row
        &.progress-row
            padding: $s
            display: grid
            width: 100%
            grid-template-columns: 1fr 4fr
            grid-template-areas: "progress text" "actions actions"
            align-items: center

            .progress-container
                grid-area: progress
                width: 64px
                height: 64px
                display: grid
                place-items: center
                margin-right: $xs

            .text-container
                grid-area: text
                .headline
                    color: $neutral-black
                .subline, .subline span
                    color: $neutral-2
                    margin-bottom: $s

            .row-actions
                grid-area: actions
                display: flex
                flex-direction: column

                .button + .button
                    margin-top: $xs

                .download-qr-code
                    padding-top: $xs
                    border-top: 1px solid $gray-2

        &.action-row
            height: 42px
            display: grid
            place-items: center
            border-top: 1px solid $gray-2

            &.primary
                .button
                    color: $primary-2
                &:hover
                    .button
                        color: darken($primary-2, 15%)

            &.danger
                .button
                    color: $red

                &:hover
                    .button
                        color: darken($red, 15%)

            .button
                color: $neutral-3

            &:hover
                //background: $gray-2
                cursor: pointer

                .button
                    color: $neutral-4
</style>
